/**
 * Fuso da agenda (fuso da clínica) — o mesmo que o back usa (AGENDA_TIMEZONE).
 *
 * Dia, hora e "hoje" da agenda são sempre lidos neste fuso, nunca no fuso do navegador: quem acessa de outro
 * estado (ou com o relógio do computador em outro fuso) vê e grava os mesmos horários que a clínica.
 * O back informa o fuso no login e no GET /api/auth/me (campo fusoAgenda); até lá vale o fuso do navegador.
 * Docs: 4clinics_backend/docs/arquitetura-pacote-agenda-consulta.md (Fuso).
 */

const fusoDoNavegador = () => Intl.DateTimeFormat().resolvedOptions().timeZone

let fusoAtual = null
const formatadores = new Map()

const fusoValido = (fuso) => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: fuso })
    return true
  } catch (e) {
    return false
  }
}

/** Define o fuso da agenda (vem do back). Valor vazio ou inválido volta ao fuso do navegador. */
export const definirFusoAgenda = (fuso) => {
  fusoAtual = fuso && fusoValido(fuso) ? String(fuso) : null
}

export const fusoAgenda = () => fusoAtual || fusoDoNavegador()

const formatadorPartes = (fuso) => {
  if (!formatadores.has(fuso)) {
    formatadores.set(
      fuso,
      new Intl.DateTimeFormat('en-US', {
        timeZone: fuso,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
    )
  }
  return formatadores.get(fuso)
}

const DIAS = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }

/** Instante → { ano, mes (1-12), dia, hora, minuto, segundo, diaSemana (1=Seg … 7=Dom) } no fuso da agenda. */
export const partesNoFuso = (data, fuso = fusoAgenda()) => {
  const p = Object.fromEntries(formatadorPartes(fuso).formatToParts(new Date(data)).map((x) => [x.type, x.value]))
  return {
    ano: Number(p.year),
    mes: Number(p.month),
    dia: Number(p.day),
    hora: Number(p.hour),
    minuto: Number(p.minute),
    segundo: Number(p.second),
    diaSemana: DIAS[p.weekday],
  }
}

/** Diferença (ms) entre o relógio do fuso e o UTC naquele instante. */
const deslocamento = (ms, fuso) => {
  const p = partesNoFuso(ms, fuso)
  return Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo) - Math.floor(ms / 1000) * 1000
}

/**
 * Relógio do fuso da agenda → instante. Mês 1-12; valores fora da faixa rolam (dia 32 = dia 1 do mês seguinte).
 * Horário de verão (o Brasil não tem desde 2019): o horário repetido fica com a 1ª ocorrência; o que não existe cai 1 h antes.
 */
export const instanteNoFuso = (ano, mes, dia, hora = 0, minuto = 0, segundo = 0, ms = 0, fuso = fusoAgenda()) => {
  const relogio = Date.UTC(ano, mes - 1, dia, hora, minuto, segundo, ms)
  if (Number.isNaN(relogio)) return new Date(NaN)
  const d1 = deslocamento(relogio, fuso)
  let t = relogio - d1
  const d2 = deslocamento(t, fuso)
  if (d2 !== d1) t = relogio - d2
  return new Date(t)
}

/** toLocale*String no fuso da agenda (pt-BR). */
export const formatarNoFuso = (data, opcoes = {}) => new Date(data).toLocaleString('pt-BR', { ...opcoes, timeZone: fusoAgenda() })
export const formatarDataNoFuso = (data, opcoes = {}) => new Date(data).toLocaleDateString('pt-BR', { ...opcoes, timeZone: fusoAgenda() })
export const formatarHoraNoFuso = (data, opcoes = { hour: '2-digit', minute: '2-digit' }) =>
  new Date(data).toLocaleTimeString('pt-BR', { ...opcoes, timeZone: fusoAgenda() })

const pad2 = (n) => String(n).padStart(2, '0')

/** 'YYYY-MM-DD' do instante (padrão: agora) no fuso da agenda. */
export const dataIsoNoFuso = (data = new Date()) => {
  const p = partesNoFuso(data)
  return `${p.ano}-${pad2(p.mes)}-${pad2(p.dia)}`
}
